import { Input, OnInit } from '@angular/core';

import { ThfPageBlockedUserReason } from './enums/thf-page-blocked-user-reason.enum';
import { ThfPageBlockedUserReasonParams } from './interfaces/thf-page-blocked-user-reason-params.interface';

const ThfPageBlockedUserReasonDefault: ThfPageBlockedUserReason = ThfPageBlockedUserReason.None;
const ThfPageBlockedUserParamsDefault: ThfPageBlockedUserReasonParams = { attempts: 5, days: 90, hours: 24 };

/**
 * @description
 *
 * O componente `thf-page-blocked-user` Ã© utilizado como template para tela de bloqueio de usuÃ¡rio.
 * Ã possÃ­vel definir entre trÃªs tipos de telas para alertar o usuÃ¡rio sobre um eventual bloqueio de login.
 *
 * Cada modelo de bloqueio possui uma imagem e texto adequados Ã  situaÃ§Ã£o.
 * Os textos das telas sÃ£o prÃ©-definidos e imutÃ¡veis, porÃ©m,
 * Ã© possÃ­vel estipular parÃ¢metros como dias, horas e tentativas de acesso esgotadas.
 *
 * Por fim, hÃ¡ propriedades para adiÃ§Ã£o de telefone e/ou email para contato e tambÃ©m a definiÃ§Ã£o para a url de retorno.
 *
 * Para que as imagens sejam exibidas corretamente, Ã© necessÃ¡rio incluir o caminho delas ao projeto. Para isso, edite
 * o *assets* no arquivo **angular.json** da aplicaÃ§Ã£o na seguinte ordem:
 * ```
 *   "assets": [
 *     "src/assets",
 *     "src/favicon.ico",
 *     {
 *       "glob": "**\/*",
 *       "input": "node_modules/@totvs/thf-theme/images",
 *       "output": "assets/images"
 *     }
 *   ]
 * ```
 *
 * _______________
 *
 * #### Praticidade
 * O `thf-page-blocked-user`, assim como suas propriedades, pode tambÃ©m ser transmitido diretamente pelas configuraÃ§Ã£os de rota e,
 * desta maneira, dispensa-se qualquer menÃ§Ã£o e/ou importaÃ§Ã£o no restante da aplicaÃ§Ã£o. O exemplo abaixo exemplifica
 * a forma dinÃ¢mica com a qual o template pode ser gerado se navegasse para uma rota denominada como `/access-denied`:
 *
 *
 * ```
 *   import { ThfPageBlockedUserComponent, ThfPageBlockedUserReason } from '@totvs/thf-templates';
 *
 *   ...
 *   const routes: Routes = [
 *     {
 *       path: 'access-denied', component: ThfPageBlockedUserComponent, data: {
 *         contactEmail: 'dev.totvs@totvs.com',
 *         contactPhone: '0800 1234 000',
 *         reason: ThfPageBlockedUserReason.ExpiredPassword,
 *         urlBack: '/home'
 *       }
 *     }
 *     ...
 *   ];
 *
 *   @NgModule({
 *     imports: [RouterModule.forRoot(routes)],
 *     exports: [RouterModule]
 *   })
 *   export class AppRoutingModule { }
 * ```
 *
 * > Ã essencial que siga a nomenclatura dos atributos exemplificados acima para sua efetiva funcionalidade.
 *
 */
export class ThfPageBlockedUserBaseComponent {

  private _params: ThfPageBlockedUserReasonParams = {...ThfPageBlockedUserParamsDefault};
  private _reason: ThfPageBlockedUserReason = ThfPageBlockedUserReason.None;
  private _urlBack: string = '/';

  /**
   * @optional
   *
   * @description
   *
   * Valor para o email de contato que deve ser exibido. A aÃ§Ã£o estÃ¡ de acordo com o protocolo MAILTO e Ã© possÃ­vel definir
   * tanto rotas internas quanto externas.
   */
  @Input('t-contact-email') contactEmail: string;

  /**
   * @optional
   *
   * @description
   *
   * Valor para o telefone de contato que deve ser exibido. A aÃ§Ã£o estÃ¡ de acordo com o protocolo TEL.
   *
   * > A propriedade nÃ£o contem tratamento de mÃ¡scara, fica a critÃ©rio do desenvolvedor defini-la.
   */
  @Input('t-contact-phone') contactPhone: string;

  /**
   * @optional
   *
   * @description
   *
   * DesignaÃ§Ã£o de valores usados para a customizaÃ§Ã£o da mensagem de bloqueio.
   * Confira abaixo os valores prÃ©-definidos.
   *
   * ```
   *  const customLiterals: ThfPageBlockedUserReasonParams = {
   *    attempts: 5,
   *    days: 90,
   *    hours: 24
   *  };
   * ```
   *
   * > Salientamos a importÃ¢ncia e atenÃ§Ã£o para configuraÃ§Ã£o desses valores conforme definidos no projeto.
   *
   * > Veja os parÃ¢metros customizÃ¡veis na interface `ThfPageBlockedUserReasonParams`.
   *
   */
  @Input('t-params') set params(value: ThfPageBlockedUserReasonParams) {
    if (value instanceof Object) {
      const keys = Object.keys(value);
      const newParams = {...ThfPageBlockedUserParamsDefault};

      keys.forEach(key => {
        newParams[key] = value[key];
      });

      this._params = newParams;
    } else {
      this._params = {...ThfPageBlockedUserParamsDefault};
    }
  }

  get params() {
    return this._params;
  }

  /** Caminho para a logomarca localizada na parte superior, caso nÃ£o seja definida ou seja invÃ¡lida assume a logo padrÃ£o da TOTVS. */
  @Input('t-logo') logo?: string;

  /**
   * @optional
   *
   * @description
   *
   * DefiniÃ§Ã£o de motivo de bloqueio de usuÃ¡rio. As informaÃ§Ãµes modificam conforme o motivo selecionado.
   *
   * > Veja os valores vÃ¡lidos no *enum* `ThfPageBlockedUserReason`.
   *
   * @default `ThfPageBlockedUserReason.None`
   */
  @Input('t-reason') set reason(value: ThfPageBlockedUserReason) {
    this._reason = (<any>Object).values(ThfPageBlockedUserReason).includes(value) ? value : ThfPageBlockedUserReasonDefault;
  }

  get reason() {
    return this._reason;
  }

  /**
   * @optional
   *
   * @description
   *
   * Caminho para a logomarca localizada no rodapÃ©.
   */
  @Input('t-secondary-logo') secondaryLogo?: string;

  /**
   * @optional
   *
   * @description
   *
   * URL para a aÃ§Ã£o de retorno da pÃ¡gina.
   *
   * @default `/`
   */
  @Input('t-url-back') set urlBack(url: string) {
    this._urlBack = url;
  }

  get urlBack() {
    return this._urlBack;
  }

}
